import { AfterViewInit, Directive, ElementRef, HostListener, inject, input, signal } from '@angular/core'
import { NgControl } from '@angular/forms'
import { filter } from 'rxjs'

export type FormatterFunction<V = unknown> = (value: V) => string
export type ParserFunction<V = unknown> = (value: string) => V

@Directive({
  selector: '[appFormatView]',
})
export class FormatViewDirective implements AfterViewInit {
  private readonly elementRef = inject(ElementRef)
  private readonly ngControl = inject(NgControl)

  readonly focusInFormatter = input<FormatterFunction>()
  readonly focusOutFormatter = input<FormatterFunction>()
  readonly changeFormatter = input<ParserFunction>()

  private readonly outputValue = signal<unknown>(undefined)

  private inputElement!: HTMLInputElement

  ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement
    this.inputElement =
      element.tagName === 'INPUT'
        ? element
        : (element.querySelector('input[type="text"], input[type="number"]') as HTMLInputElement)

    if (this.inputElement?.value) {
      this.inputElement.value = this.getFocusOutValue(this.inputElement.value)
    }

    this.ngControl?.valueChanges
      ?.pipe(filter((value) => value !== this.outputValue() && value !== this.inputElement.value))
      .subscribe((value) => {
        this.inputElement.value = this.getFocusOutValue(value)
      })
  }

  @HostListener('focusin')
  onFocusIn(): void {
    const parsedValue = this.getChangeValue(this.inputElement.value)
    this.inputElement.value = this.getFocusInValue(parsedValue)
  }

  @HostListener('focusout')
  onFocusOut(): void {
    const formatted = this.getFocusOutValue(this.inputElement.value)
    this.outputValue.set(this.getChangeValue(formatted))
    if (this.ngControl?.control && this.ngControl.enabled && this.ngControl.value !== this.outputValue()) {
      this.ngControl.control.setValue(this.outputValue())
    }
    this.inputElement.value = formatted
  }

  private getFocusInValue(value: unknown): string {
    if (value === null || value === undefined) return ''
    if (!this.focusInFormatter()) return value.toString()
    return (this.focusInFormatter() as FormatterFunction)(value)
  }

  private getFocusOutValue(value: unknown): string {
    if (value === null || value === undefined) return ''
    if (!this.focusOutFormatter()) return value.toString()
    return (this.focusOutFormatter() as FormatterFunction)(value)
  }

  private getChangeValue(value: string): unknown {
    if (value === null || value === undefined || value === '') return null
    if (!this.changeFormatter()) return value
    return (this.changeFormatter() as ParserFunction)(value)
  }
}
